import React, { useEffect, useState, useCallback, useMemo, memo } from 'react';
import axios from 'axios';
import { useLabels, useLang } from '../hooks/useLabels';
import ToggleButton from 'react-bootstrap/ToggleButton';
import UserTopbar from '../component/UserTopbar'
import { BiBarcodeReader, BiChevronRight, BiChevronLeft, BiBarChart } from "react-icons/bi";
import { BiCheckCircle, BiXCircle } from "react-icons/bi";
import ProgressBar from 'react-bootstrap/ProgressBar';
import { BiCalendar } from "react-icons/bi";
import { FcOk, FcHighPriority, FcFinePrint, FcLeave } from "react-icons/fc";
import { useParams, useNavigate } from "react-router-dom";
import Swal from 'sweetalert2';
import moment from 'moment-timezone';
import { callMsGraph, checkIsAdmin } from '../callMsGroup';
import LoadingScreen from '../component/LoadingScreen';
import { getImageSrc } from '../utils/imageSrc';
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { acquireGetAccessToken } from '../utils/GetGrapApi'
import { Buffer } from 'buffer';
import { getUserEmails, isOwnerOf } from '../utils/ownerMatch';


// Escape HTML special chars before inserting API-sourced values into Swal html: strings
const esc = (s: unknown): string =>
    String(s ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c] ?? c));

// Upcoming modal labels (hardcoded — no DB dependency)
const UPCOMING_LABELS = {
    title:     { en: 'Event Not Yet Started',                                                           vn: 'Sự Kiện Chưa Bắt Đầu' },
    desc:      { en: 'This event has not started yet. Please come back on the start date.',             vn: 'Sự kiện này chưa bắt đầu. Vui lòng quay lại vào ngày bắt đầu.' },
    starts_on: { en: 'Starts on',                                                                       vn: 'Bắt đầu lúc' },
    go_back:   { en: 'Go Back',                                                                         vn: 'Quay Lại' },
};


interface UserProfile {
    name: string;
    employeeId: string;
    usR_PERSONID: string;
    date: string;
}

interface Employee {
    id: number;
    eventId: number;
    employeeId: string;
    recieved: string;
    takeRecieved: string;
    updatedBy: string;
    updatedAt: string;
    reward: any[];
    employee: {
        employeeId: string;
        displayName: string;
        namempt: string;
        nampost: string;
        jobTitle: string;
        officeLocation: string;
        codcom2: string;
        [key: string]: any;
    };
}

interface EventData {
    id: number;
    eventNumber: string;
    eventName: string;
    description: string;
    startDate: string;
    endDate: string;
    confirmStatus: string;
    behalStatus: string;
    totalRecieved: number;
    totalMember: number;
    owner: string;
    eventRewards: any[];
}

function Event() {
    let { eventnumber }: any = useParams();
    
    // Combined related states
    const [appState, setAppState] = useState({
        loading: false,
        loadingPage: false,
        success: false,
        duplicate: false,
        stateLoad: false,
        onBehalfCheck: false,
        onBehalf: true,
        confirmEvent: true
    });

const [sidebarOpen, setSidebarOpen] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isUpcoming, setIsUpcoming] = useState(false);

    // Form states
    const [empId, setEmpId] = useState("");
    const [tempEmpId, setTempEmpId] = useState("");
    
    // Auth states
    const [authState, setAuthState] = useState({
        token: "",
        tokenProfile: "",
        photoData: "",
        profile: { jobTitle: "", mail: "", userPrincipalName: "" }
    });

    // Data states
    const [userProfileR, setProfileR] = useState<UserProfile>({
        name: "",
        employeeId: "",
        usR_PERSONID: "",
        date: ""
    });

    const [employee, setEmployee] = useState<Employee>({
        id: 0,
        eventId: 0,
        employeeId: "",
        recieved: "",
        takeRecieved: "",
        updatedBy: "",
        updatedAt: "",
        reward: [],
        employee: {
            employeeId: "",
            displayName: "",
            namempt: "",
            nampost: "",
            jobTitle: "",
            officeLocation: "",
            codcom2: ""
        }
    });

    const [eventdata, setEventdata] = useState<EventData>({
        id: 0,
        eventNumber: "",
        eventName: "",
        description: "",
        startDate: "",
        endDate: "",
        confirmStatus: "",
        behalStatus: "",
        totalRecieved: 0,
        totalMember: 0,
        owner: "",
        eventRewards: []
    });

    const { inProgress, instance, accounts } = useMsal();
    const navigate = useNavigate();

    const [lang, setLang] = useLang();
    const { t } = useLabels('event', authState.token, lang);
    const U = (key: keyof typeof UPCOMING_LABELS) => UPCOMING_LABELS[key][lang];

    // Memoized auth headers
    const auths = useMemo(() => ({
        headers: {
            "Content-type": "application/json",
            'Authorization': 'Bearer ' + (sessionStorage.getItem("accessToken") || authState.token)
        }
    }), [authState.token]);

    // Memoized date conversion functions
    const convertToG7 = useCallback((date: any) => {
        if (!date) return '';
        let formattedDate;
        
        if (typeof date === 'string' && date.includes('Z')) {
            formattedDate = moment(date);
        } else {
            formattedDate = moment.utc(date);
        }
        return formattedDate.tz('Asia/Bangkok').format('DD-MM-YYYY HH:mm:ss');
    }, []);

    const convertToG72 = useCallback((date: any) => {
        if (!date) return '';
        return moment.utc(date).tz('Asia/Bangkok').format('DD-MMM-YYYY');
    }, []);

    const convertToCommaSeparated = useCallback((number: number) => {
        return number.toLocaleString();
    }, []);

    // Memoized progress calculation
    const progressPercentage = useMemo(() => {
        if (!eventdata.totalMember) return 0;
        return Math.round((eventdata.totalRecieved / eventdata.totalMember) * 100);
    }, [eventdata.totalRecieved, eventdata.totalMember]);

    // Optimized event handlers with useCallback
    const getUserInfo = useCallback(async (employeeId: any) => {
        try {
            const result = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/v1/Member/getUserInfo?EmployeeID=${employeeId}`,
                auths
            );
            return result.data;
        } catch (error: any) {
            console.error('Error fetching user info:', error);
            
            // Handle 401 Unauthorized
            if (error.response?.status === 401) {
                console.error('Token expired or unauthorized, attempting to refresh...');
                // Try to refresh token
                try {
                    const newTokens = await acquireGetAccessToken(instance, accounts);
                    if (newTokens && newTokens.accessToken) {
                        sessionStorage.setItem("accessToken", newTokens.accessToken);
                        
                        // Retry with new token
                        const retryResult = await axios.get(
                            `${process.env.REACT_APP_API_END_POINT}/v1/Member/getUserInfo?EmployeeID=${employeeId}`,
                            {
                                headers: {
                                    "Content-type": "application/json",
                                    'Authorization': 'Bearer ' + newTokens.accessToken
                                }
                            }
                        );
                        return retryResult.data;
                    }
                } catch (refreshError) {
                    console.error('Failed to refresh token:', refreshError);
                    return null;
                }
            }
            
            return null;
        }
    }, [auths, instance, accounts]);

    // Load total members count
    const loadTotalMembers = useCallback(async (eventId: number) => {
        if (!eventId) return;
        
        try {
            const url = `${process.env.REACT_APP_API_END_POINT}/v1/member/Members?eventId=${eventId}&offset=0&limit=999999`;
            const response = await axios.get(url, auths);
            
            const total = response.data.total || 0;
            const received = response.data.memberLists?.filter((m: any) => m.recieved === 1 || m.recieved === '1').length || 0;
            
            // Update eventdata with correct totals
            setEventdata(prev => ({
                ...prev,
                totalMember: total,
                totalRecieved: received
            }));
        } catch (error) {
            console.error('Error loading total members:', error);
        }
    }, [auths]);

    const onRecieved = useCallback(async (takeRecieved: any) => {
        setAppState(prev => ({ ...prev, loading: true }));
        setTempEmpId(empId);
        
        const params = takeRecieved ? {
            eventNumber: eventnumber,
            employeeId: empId,
            takeRecieved: takeRecieved,
        } : {
            eventNumber: eventnumber,
            employeeId: empId
        };
        
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_END_POINT}/v1/Events/recieved`,
                JSON.stringify(params),
                auths
            );
            
            setEmployee(response.data);
            setProfileR(response.data.takeRecievedData ? {
                name: response.data.takeRecievedData.displayName || response.data.takeRecievedData.namempt || "",
                employeeId: response.data.takeRecievedData.employeeId || "",
                usR_PERSONID: response.data.takeRecievedData.usR_PERSONID || response.data.takeRecievedData.employeeId || "",
                date: response.data.takeRecievedData.updatedAt
            } : { name: "", employeeId: "", usR_PERSONID: "", date: "" });
            setEmpId("");
            setAppState(prev => ({ ...prev, loading: false, duplicate: false }));
            
            // Reload total members count after successful receipt
            if (eventdata.id) {
                loadTotalMembers(eventdata.id);
            }
        } catch (error) {
            console.error('Error recording receipt:', error);
            setEmpId("");
            setAppState(prev => ({ ...prev, loading: false, duplicate: false }));
        }
    }, [empId, eventnumber, auths, eventdata.id, loadTotalMembers]);

    const onValidate = useCallback(async (takeRecieved: any) => {
        setAppState(prev => ({ ...prev, loading: true }));
        setTempEmpId(empId);
        
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/v1/Events/validateEmployee?eventId=${eventdata.id}&employeeId=${empId}`,
                auths
            );
            
            // Check for error message in response
            if (response.data?.Message || response.data?.message) {
                const errorMsg = response.data.Message || response.data.message;
                Swal.fire({
                    title: 'Error',
                    text: errorMsg,
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
                setEmpId("");
                setAppState(prev => ({ ...prev, loading: false, duplicate: false }));
                return;
            }
            
            if (response.data.length === 0) {
                await onRecieved(takeRecieved);
            } else {
                if (response.data.takeRecievedData) {
                    setProfileR({
                        name: response.data.takeRecievedData.displayName || response.data.takeRecievedData.namempt || "",
                        employeeId: response.data.takeRecievedData.employeeId || "",
                        usR_PERSONID: response.data.takeRecievedData.usR_PERSONID || response.data.takeRecievedData.employeeId || "",
                        date: response.data.takeRecievedData.updatedAt,
                    });
                } else {
                    setProfileR({ name: "", employeeId: "", usR_PERSONID: "", date: "" });
                }
                setEmployee(response.data);
                setEmpId("");
                setAppState(prev => ({ ...prev, loading: false, duplicate: true }));
            }
        } catch (error: any) {
            console.error('Error validating employee:', error);
            
            // Check for API error message
            let errorMessage = 'An error occurred while verifying data';
            
            if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail.ErrorTh || error.response.data.detail.Message || errorMessage;
            } else if (error.response?.data?.Message) {
                errorMessage = error.response.data.Message;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            
            Swal.fire({
                title: 'Error',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'OK'
            });
            
            setEmpId("");
            setAppState(prev => ({ ...prev, loading: false, duplicate: false }));
        }
    }, [empId, eventdata.id, auths, onRecieved]);

    const handleModalonBehalf = useCallback(async (userInfo: any) => {
        // Validate userInfo before proceeding
        if (!userInfo || !userInfo.employeeId) {
            Swal.fire({
                title: 'Error',
                text: 'Employee ID not found. Please verify before selecting a substitute.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            setEmpId("");
            setAppState(prev => ({ ...prev, onBehalfCheck: false }));
            return;
        }

        const ownerName = userInfo.displayName || userInfo.namempt || userInfo.nampost || 'Unknown';
        const ownerEmpId = userInfo.employeeId || userInfo.usR_PERSONID;
        
        try {
            const ownerInitial = ownerName.charAt(0).toUpperCase();
            const result = await Swal.fire({
                title: 'Receive on Behalf',
                html: `
                    <style>
                        #swal-proxy-input::-webkit-outer-spin-button,
                        #swal-proxy-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
                        #swal-proxy-input[type=number] { -moz-appearance: textfield; }
                        #swal-proxy-input:focus { border-color: #c5281f !important; box-shadow: 0 0 0 3px #fbe1de; }
                    </style>
                    <div style="text-align:left; padding:0 4px;">
                        <div style="background:#fdf3f2; border-radius:10px; padding:12px 14px; margin-bottom:16px; border-left:4px solid #c5281f;">
                            <p style="font-size:10px; font-weight:700; color:#7a716d; text-transform:uppercase; letter-spacing:0.08em; margin:0 0 10px;">Owner Employee</p>
                            <div style="display:flex; align-items:center; gap:10px;">
                                <div style="width:38px; height:38px; background:linear-gradient(to bottom right,#c5281f,#a02018); border-radius:50%; display:flex; align-items:center; justify-content:center; color:#fff; font-weight:700; font-size:15px; flex-shrink:0;">${esc(ownerInitial)}</div>
                                <div>
                                    <p style="margin:0; font-weight:600; font-size:14px; color:#1f2937;">${esc(ownerName)}</p>
                                    <p style="margin:2px 0 0; font-size:12px; color:#6b7280;">Employee ID: ${esc(ownerEmpId)}</p>
                                </div>
                            </div>
                        </div>
                        <div>
                            <p style="font-size:12px; font-weight:600; color:#374151; margin:0 0 6px; text-transform:uppercase; letter-spacing:0.05em;">Proxy Recipient ID</p>
                            <input id="swal-proxy-input" type="number" placeholder="Enter employee ID"
                                style="width:100%; box-sizing:border-box; border:1.5px solid #d1d5db; border-radius:8px; padding:10px 12px; font-size:14px; outline:none; transition:border-color 0.2s, box-shadow 0.2s; color:#1f2937;"/>
                            <div id="swal-proxy-name" style="min-height:24px; font-size:13px; margin-top:6px; color:#374151; display:flex; align-items:center; gap:5px;"></div>
                        </div>
                    </div>
                `,
                showCancelButton: true,
                confirmButtonText: 'Confirm',
                cancelButtonText: 'Cancel',
                confirmButtonColor: '#c5281f',
                cancelButtonColor: '#a59d99',
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false,
                didOpen: () => {
                    const input = document.getElementById('swal-proxy-input') as HTMLInputElement | null;
                    const nameEl = document.getElementById('swal-proxy-name');
                    if (!input || !nameEl) return;
                    let debounceTimer: any;
                    const setNameEl = (opts: { type: 'loading' | 'found' | 'error'; text: string }) => {
                        nameEl.textContent = '';
                        if (opts.type === 'found') {
                            const icon = document.createElement('span');
                            icon.style.cssText = 'color:#16a34a; font-size:18px; line-height:1;';
                            icon.textContent = '✓';
                            const label = document.createElement('span');
                            label.style.cssText = 'color:#15803d; font-weight:500;';
                            label.textContent = opts.text;
                            nameEl.appendChild(icon);
                            nameEl.appendChild(label);
                        } else {
                            const span = document.createElement('span');
                            span.style.cssText = opts.type === 'error'
                                ? 'color:#dc2626; font-size:13px;'
                                : 'color:#9ca3af; font-size:12px;';
                            span.textContent = opts.text;
                            nameEl.appendChild(span);
                        }
                    };
                    input.addEventListener('input', (e: any) => {
                        clearTimeout(debounceTimer);
                        nameEl.textContent = '';
                        const val = e.target.value?.trim();
                        if (!val || val.length < 7) return;
                        setNameEl({ type: 'loading', text: 'Looking up…' });
                        debounceTimer = setTimeout(async () => {
                            try {
                                const info = await getUserInfo(val);
                                const name = info?.displayName || info?.namempt || info?.nampost || '';
                                name
                                    ? setNameEl({ type: 'found', text: name })
                                    : setNameEl({ type: 'error', text: 'Employee not found' });
                            } catch {
                                setNameEl({ type: 'error', text: 'Employee not found' });
                            }
                        }, 400);
                    });
                },
                preConfirm: async () => {
                    const input = document.getElementById('swal-proxy-input') as HTMLInputElement | null;
                    const value = input?.value?.trim();
                    if (!value || isNaN(Number(value))) {
                        Swal.showValidationMessage('Please enter the substitute employee ID');
                        return false;
                    }
                    if (value === String(ownerEmpId)) {
                        Swal.showValidationMessage('Cannot use the same employee ID');
                        return false;
                    }
                    const userProfileInfo = await getUserInfo(value);
                    if (!userProfileInfo || !userProfileInfo.employeeId) {
                        Swal.showValidationMessage('Substitute employee ID not found');
                        return false;
                    }
                    return value;
                }
            });

            if (result.isConfirmed) {
                await onValidate(result.value);
                setAppState(prev => ({ ...prev, onBehalfCheck: false }));
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                setEmpId("");
                setAppState(prev => ({ ...prev, onBehalfCheck: false }));
            }
        } catch (error) {
            console.error('Error in behalf modal:', error);
            setEmpId("");
            setAppState(prev => ({ ...prev, onBehalfCheck: false }));
        }
    }, [getUserInfo, onValidate]);
    
    const handleModalConfirm = useCallback(async (userInfo: any) => {
        const hasValidUserInfo = userInfo && userInfo.employeeId;
        
        if (!hasValidUserInfo) {
            Swal.fire({
                title: 'Employee Not Found',
                text: 'Employee ID not found. Please verify the employee ID.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
            setEmpId("");
            return;
        }
        
        try {
            const result = await Swal.fire({
                title: 'Confirm Receive',
                html: `
                    <div style="text-align: left; padding: 10px;">
                        <p><strong>Employee ID:</strong> ${esc(userInfo.employeeId || userInfo.usR_PERSONID)}</p>
                        <p><strong>Name:</strong> ${esc(userInfo.displayName || userInfo.namempt || userInfo.nampost || 'Unknown')}</p>
                        <p><strong>Department:</strong> ${esc(userInfo.department || userInfo.namcenteng || userInfo.namcentth || 'Unknown')}</p>
                        <hr style="margin: 15px 0;">
                        <p>Please confirm to receive</p>
                    </div>
                `,
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Confirm Receive',
                cancelButtonText: 'Cancel',
                allowOutsideClick: false,
                allowEscapeKey: false,
                allowEnterKey: false
            });

            if (result.isConfirmed) {
                await onValidate(null);
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                setEmpId("");
            }
        } catch (error) {
            console.error('Error in confirm modal:', error);
            setEmpId("");
        }
    }, [onValidate]);

    const hendelChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value; 
        setEmpId(value);
    }, []);

    const hendleEnter = useCallback(async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.keyCode === 13) {
            // Don't proceed if employee ID is empty
            if (!empId.trim()) {
                return;
            }

            setProfileR({ name: "", employeeId: "", usR_PERSONID: "", date: "" });

            // Optimize: Only fetch user info when actually needed
            // Accept 7-digit EmployeeId OR 5-digit CardNo (from card scanner)
            const validLen = empId.length === 7 || empId.length === 5;
            const shouldFetchUserInfo = (appState.confirmEvent && !appState.onBehalfCheck && validLen) || appState.onBehalfCheck;
            // console.log('shouldFetchUserInfo:', shouldFetchUserInfo, {
            //     confirmEvent: appState.confirmEvent,
            //     onBehalfCheck: appState.onBehalfCheck,
            //     empIdLength: empId.length,
            //     empId: empId
            // });
            
            if (!shouldFetchUserInfo) {
                // Direct validation - fastest path
                onValidate(null);
                return;
            }
            
            // Fetch user info only when needed
            const userInfo = await getUserInfo(empId);
            
            if (appState.onBehalfCheck) {
                // Handle behalf mode
                if (!userInfo) {
                    Swal.fire({
                        title: 'Error',
                        text: 'Employee ID not found. Please verify again.',
                        icon: 'error',
                        confirmButtonText: 'OK'
                    });
                    return;
                }
                handleModalonBehalf(userInfo);
            } else {
                // Show confirmation modal for short employee IDs
                handleModalConfirm(userInfo);
            }
        }
    }, [empId, appState.confirmEvent, appState.onBehalfCheck, getUserInfo, handleModalConfirm, handleModalonBehalf, onValidate]);


    // Optimized data loading with useCallback
    const loadData = useCallback(async () => {
        setAppState(prev => ({ ...prev, loadingPage: true }));
        
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_END_POINT}/v1/events/eventactive?eventNumber=${eventnumber}`,
                auths
            );
            
            if (response.data && response.data.id) {
                setEventdata(response.data);
                // Check if event hasn't started yet
                if (response.data.startDate && moment().isBefore(moment(response.data.startDate))) {
                    setIsUpcoming(true);
                }
                setAppState(prev => ({ 
                    ...prev, 
                    confirmEvent: response.data.confirmStatus === '1',
                    onBehalf: response.data.behalStatus === '1',
                    success: true,
                    loadingPage: false,
                    stateLoad: true
                }));
                
                // Load actual member counts
                loadTotalMembers(response.data.id);
            } else {
                // Event not found or not active
                setAppState(prev => ({ 
                    ...prev, 
                    success: false,
                    loadingPage: false,
                    stateLoad: true
                }));
            }
        } catch (error: any) {
            console.error('Error loading event data:', error);
            
            // Set state first to stop loading
            setAppState(prev => ({ 
                ...prev, 
                success: false, 
                loadingPage: false,
                stateLoad: true
            }));
            
            // Check if it's an API error response
            let errorMessage = 'Sorry, this event is not active or the data you are looking for was not found.';
            
            if (error.response?.data?.detail) {
                // Use the Thai error message from API if available
                errorMessage = error.response.data.detail.ErrorTh || error.response.data.detail.Message || errorMessage;
            }
            
            // Show error message to user after state is updated
            Swal.fire({
                title: 'Event Not Found',
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    }, [eventnumber, auths, loadTotalMembers]);

    // Auth token acquisition effect
    useEffect(() => {
        if (!authState.token && inProgress === InteractionStatus.None) {
            acquireGetAccessToken(instance, accounts).then(async (res: any) => {
                try {
                    // Get photo — users without a profile photo get 404, which must not block loading the profile
                    let photoData = "";
                    try {
                        const photoResponse = await axios("https://graph.microsoft.com/v1.0/me/photo/$value", {
                            headers: { Authorization: `Bearer ${res.accessTokenProfile}` },
                            responseType: "arraybuffer"
                        });
                        const avatar = Buffer.from(photoResponse.data, "binary").toString("base64");
                        photoData = "data:image/jpeg;base64, " + avatar;
                    } catch { /* no photo */ }

                    // Get profile
                    const profileResponse = await axios("https://graph.microsoft.com/v1.0/me", {
                        headers: { Authorization: `Bearer ${res.accessTokenProfile}` }
                    });

                    setAuthState({
                        token: res.accessToken,
                        tokenProfile: res.accessTokenProfile,
                        photoData: photoData,
                        profile: profileResponse.data
                    });
                } catch (error) {
                    console.error('Error fetching auth data:', error);
                    setAuthState(prev => ({
                        ...prev,
                        token: res.accessToken,
                        tokenProfile: res.accessTokenProfile
                    }));
                }
            });
        }
    }, [inProgress, authState.token, instance, accounts]);

    // Role checking effect with optimization
    useEffect(() => {
        const checkRole = async () => {
            if (eventdata.owner && (authState.profile.mail || authState.profile.userPrincipalName) && appState.success) {
                try {
                    const ownerExists = isOwnerOf(eventdata.owner, getUserEmails(authState.profile, accounts[0]));
                    
                    const groupExists = await checkIsAdmin(authState.tokenProfile);

                    if (groupExists || ownerExists) {
                        setIsAdmin(groupExists);
                        setAppState(prev => ({ ...prev, stateLoad: true }));
                    } else {
                        window.location.href = `${window.location.origin}/accessdenied`;
                    }
                } catch (error) {
                    console.error('Error checking role:', error);
                }
            }
        };

        checkRole();
    }, [eventdata.owner, authState.profile.mail, authState.profile.userPrincipalName, authState.tokenProfile, appState.success]);

    // Load data when token is available
    useEffect(() => {
        if (authState.token) {
            loadData();
        }
    }, [authState.token, loadData]);

    // Early return for loading state
    if (appState.loadingPage) {
        return <LoadingScreen />;
    }

    return (
        <>
        {
            (eventdata.eventNumber !== "" && appState.stateLoad) ? <>
                {/* Toggle tab — small button on left edge */}
                <button
                    className={`sidebar-toggle-tab${sidebarOpen ? ' sidebar-toggle-tab--shifted' : ''}`}
                    onClick={() => setSidebarOpen(o => !o)}
                    aria-label="Toggle menu"
                >
                    {sidebarOpen ? <BiChevronLeft size={14} /> : <BiChevronRight size={14} />}
                </button>

                {/* Overlay — click outside sidebar to close */}
                {sidebarOpen && (
                    <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
                )}

                <UserTopbar token={authState.token} photoData={authState.photoData} collapsible open={sidebarOpen} isAdmin={isAdmin} showLang />

                {/* Upcoming Event Lock Modal */}
                {isUpcoming && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        background: 'rgba(24,21,19,0.65)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <div style={{
                            background: 'var(--n-0)', borderRadius: 'var(--r-lg)', padding: '40px 36px',
                            maxWidth: 420, width: '90%', textAlign: 'center',
                            boxShadow: 'var(--sh-lg)',
                        }}>
                            <div style={{ fontSize: 52, marginBottom: 12 }}>🔒</div>
                            <h4 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--n-900)', fontFamily: 'var(--font-sans)' }}>
                                {U('title')}
                            </h4>
                            <p style={{ color: 'var(--n-500)', marginBottom: 20, lineHeight: 'var(--lh-normal)', fontSize: 'var(--fs-sm)' }}>
                                {U('desc')}
                            </p>
                            <div style={{
                                background: 'var(--primary-50)', border: '1px solid var(--primary-100)',
                                borderRadius: 'var(--r-md)', padding: '12px 20px',
                                display: 'inline-block', marginBottom: 28,
                            }}>
                                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--n-400)', display: 'block', marginBottom: 4 }}>
                                    {U('starts_on')}
                                </span>
                                <span style={{ fontWeight: 700, fontSize: 'var(--fs-xl)', color: 'var(--primary-600)' }}>
                                    {moment.utc(eventdata.startDate).tz('Asia/Bangkok').format('DD MMM YYYY, HH:mm')}
                                </span>
                            </div>
                            <br />
                            <button
                                onClick={() => navigate(-1)}
                                style={{
                                    padding: '10px 32px',
                                    background: 'var(--brand-grad)',
                                    color: 'var(--n-0)', border: 'none', borderRadius: 'var(--r-sm)',
                                    fontWeight: 600, fontSize: 'var(--fs-base)', cursor: 'pointer',
                                    boxShadow: 'var(--sh-brand)',
                                }}
                            >
                                {t('upcoming.go_back', U('go_back'))}
                            </button>
                        </div>
                    </div>
                )}

                <div className='container-fluid event-bg p-0'>
                    <div className='container pt-4'>
                        {
                            (eventdata.eventNumber !== "" && appState.stateLoad) ?
                                <div className='row row-form'>
                    <div className='col-12 col-md-6'>
                                        <div className='event-info p-5'>
                                            <ProgressBar
                                                now={progressPercentage}
                                                label={`${progressPercentage}%`}
                                                style={{ background: 'var(--primary-100)' }}
                                                className="progress-crimson"
                                            />
                                            <div className='w-100 text-end mt-1'>
                                                {convertToCommaSeparated(eventdata.totalRecieved)} / {convertToCommaSeparated(eventdata.totalMember)} persons 
                                            </div>
                                            <br />
                                            <h1>{eventdata.eventName}</h1>
                                            <div className='date'>
                                                <BiCalendar size={22} />
                                                <span className='me-1'>
                                                    {convertToG72(eventdata.startDate)}
                                                </span>
                                                -
                                                <span className='ms-1'>
                                                    {convertToG72(eventdata.endDate)}
                                                </span>
                                            </div>
                                            <p className='mt-3'>{eventdata.description}</p>
                                            {isAdmin && (
                                                <div className='mt-3'>
                                                    <a
                                                        href={`/event/${eventnumber}/report`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className='btn btn-outline-secondary btn-sm'
                                                    >
                                                        <BiBarChart size={16} className='me-1' />
                                                        {t('btn.view_report', 'View Report')}
                                                    </a>
                                                </div>
                                            )}
                                            <div className='mt-4 mb-2'>
                                                {t('label.gift_items', 'Gift Items')}
                                            </div>
                                            <table className="table items-table-user mt-4">
                                                <tbody>
                                                    {
                                                        (eventdata.eventRewards.length > 0) ?
                                                            eventdata.eventRewards.map(
                                                                (item: any,
                                                                    index: number) => (
                                                                    <tr key={index}>
                                                                        <td>
                                                                            <img src={getImageSrc(item?.image)} alt={item?.rewardName || 'reward'} />
                                                                        </td>
                                                                        <td>{item?.rewardName} <p>{t('label.qty', 'Qty')}: {item?.qty} </p></td>
                                                                    </tr>
                                                                )
                                                            )
                                                            :
                                                            <></>
                                                    }
                                                </tbody>
                                            </table>

                                        </div>
                                    </div>
                                    <div className='col-12 col-md-6 p-0 ps-md-0'>

                                        <div className='register-form'>
                                            <div className='mb-2'>
                                                <label className='mb-0'>{t('label.enter_employee_id', 'Please enter employee ID')}</label> <span className='text-danger'> ({t('label.seven_digits', '7 digits')}) / {t('label.scan_card', 'scan card')}</span>
                                            </div>

                                            <div className="input-group">
                                                <input 
                                                    type="text" 
                                                    className="form-control" 
                                                    aria-describedby="basic-addon2" 
                                                    onChange={hendelChange} 
                                                    onKeyDown={hendleEnter}
                                                    value={empId} 
                                                    autoFocus={true}
                                                    pattern="[0-9]*"
                                                    inputMode="numeric"
                                                    maxLength={10}
                                                    placeholder={t('placeholder.numbers_only', 'Numbers only')}
                                                    onBeforeInput={(e: React.FormEvent<HTMLInputElement>) => {
                                                        const input = (e.nativeEvent as InputEvent).data;
                                                        if (input && !/^\d+$/.test(input)) {
                                                            e.preventDefault();
                                                            
                                                            if (/[\u0E00-\u0E7F]/.test(input)) {
                                                                Swal.fire({
                                                                    title: 'Notice',
                                                                    text: 'Numbers only. Thai characters are not allowed.',
                                                                    icon: 'warning',
                                                                    showConfirmButton: false,
                                                                    timer: 1500,
                                                                    timerProgressBar: true,
                                                                    position: 'top-end',
                                                                    toast: true
                                                                });
                                                            }
                                                        }
                                                    }}
                                                    onPaste={(e) => {
                                                        // Prevent pasting non-numeric values
                                                        e.preventDefault();
                                                        const pastedData = e.clipboardData.getData('text');
                                                        if (/^\d+$/.test(pastedData)) {
                                                            const newValue = pastedData.slice(0, 10); // Limit to max 10 characters
                                                            setEmpId(newValue);
                                                        }
                                                    }}
                                                />
                                                { appState.onBehalf ? ( 
                                                <ToggleButton
                                                    id="toggle-check"
                                                    type="checkbox"
                                                    variant="outline-primary"
                                                    checked={appState.onBehalfCheck}
                                                    value="1"
                                                    onChange={(e) => {
                                                        const isChecked = e?.currentTarget?.checked ?? false;
                                                        setAppState(prev => ({ 
                                                            ...prev, 
                                                            onBehalfCheck: isChecked 
                                                        }));
                                                    }}
                                                >
                                                    {t('btn.substitute', 'Substitute')}
                                                </ToggleButton>
                                                ) : null}
                                                <span className="input-group-text" id="basic-addon2" onClick={() => onValidate(null)}>
                                                    <BiBarcodeReader size={26} />
                                                </span>
                                            </div>
                                        </div>


                                        <div className='result-form text-center p-5 pt-4 position-relative'>
                                            {
                                                (tempEmpId === "") ?
                                                    <>
                                                        <FcFinePrint size={96} />
                                                        <h2 className='mt-3 text-secondary'>{t('label.welcome', 'Welcome')}</h2>
                                                    </>
                                                    : <>
                                                        {
                                                            (!appState.loading) ?
                                                                (employee.employeeId !== "" && employee.recieved === "1") ?
                                                                    <>
                                                                        {
                                                                            (appState.duplicate) ?
                                                                                <>
                                                                                    <FcHighPriority className='text-warning' size={96} />
                                                                                    <h2 className='mt-3 text-warning'>{t('state.already_registered', 'Already Registered')}</h2>
                                                                                    <p>
                                                                                        {t('state.verify_again', 'Please verify the employee ID again')}
                                                                                    </p>
                                                                                </>
                                                                                : <>
                                                                                    <FcOk size={72} />
                                                                                    <h4 className='mt-3'>{t('state.data_saved', 'Data Saved')}</h4>
                                                                                    <p>
                                                                                        {t('state.data_saved_msg', 'Your information has been saved successfully')}
                                                                                    </p>
                                                                                </>
                                                                        }

                                                                        <div className='row mt-4 border-top '>
                                                                            <div className='col-6  text-start headline'>
                                                                                {t('label.employee_id', 'Employee ID')}
                                                                            </div>
                                                                            <div className='col-6  text-start headline'>
                                                                                {t('label.full_name', 'Full Name')}
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start '>
                                                                                {employee.employeeId}
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                {employee.employee.displayName || employee.employee.namempt}
                                                                            </div>
                                                                            <div className='col-6  text-start headline'>
                                                                                {t('label.position', 'Position')}
                                                                            </div>
                                                                            <div className='col-6  text-start headline'>
                                                                                {t('label.building', 'Building')}
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                {employee.employee.nampost || employee.employee.jobTitle}
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                {employee.employee.officeLocation || employee.employee.codcom2}
                                                                            </div>
                                                                            {(!userProfileR?.employeeId?.length) ? 
                                                                            <>
                                                                            <div className='col-6  text-start headline'>
                                                                                {t('label.received_date', 'Received Date')}
                                                                            </div>
                                                                            <div className='col-6  text-start headline'>
                                                                            
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                {convertToG7(employee.updatedAt)}
                                                                            </div>
                                                                            <div className='col-6 mb-3 text-start'>
                                                                                
                                                                            </div>
                                                                            </>:<></>
                                                                            }
                                                                
                                                                            {
                                                                                (userProfileR?.employeeId?.length > 0 ) ?  
                                                                                    <div className='col-12'>
                                                                                        <div className='mt-4 mb-2 text-start fw-bold'>
                                                                                            {t('label.received_by_substitute', 'Received By (Substitute)')}
                                                                                        </div>
                                                                                        <div className='col-12 mb-3 text-start text-danger'>
                                                                                            {userProfileR?.name} ({t('label.employee_id', 'Employee ID')} :  {userProfileR?.usR_PERSONID})
                                                                                        </div>
                                                                                        <div className='mt-4 mb-2 text-start fw-bold'>
                                                                                            {t('label.substitute_date', 'Substitute Date')}
                                                                                        </div>
                                                                                        <div className='col-12 mb-3 text-start text-danger'>
                                                                                            {convertToG7(employee.updatedAt)}
                                                                                        </div>
                                                                                    </div>
                                                                                : <></>
                                                                            }
                                                                            {
                                                                                (employee.reward.length > 0) ?
                                                                                    <div className='col-12'>
                                                                                        <div className='mt-4 mb-2 text-start fw-bold'>
                                                                                            {t('label.received_items', 'Received Items')}
                                                                                        </div>
                                                                                        <table className="table items-table-user mt-4">
                                                                                            <tbody>
                                                                                                {
                                                                                                    employee.reward.map(
                                                                                                        (item: any, index: number) => (
                                                                                                            <tr key={index}>
                                                                                                                <td>
                                                                                                                    <img src={getImageSrc(item?.image)} alt="reward" />
                                                                                                                </td>
                                                                                                                <td>{item?.rewardName} <p>{t('label.qty', 'Qty')}: {item?.qty} </p></td>
                                                                                                            </tr>
                                                                                                        )
                                                                                                    )
                                                                                                }
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                    : <></>
                                                                            }

                                                                        </div>
                                                                    </>
                                                                    : <>
                                                                        <FcHighPriority size={96} />
                                                                        <h2 className='mt-3 text-danger'>{t('label.employee_id', 'Employee ID')} {tempEmpId} {t('state.not_found', 'not found')}</h2>
                                                                        <p>
                                                                            {t('state.verify_again', 'Please verify the employee ID again')}
                                                                        </p>
                                                                    </>
                                                                :
                                                                <div className='findEmp'>
                                                                    <div className='dots'></div>
                                                                    <label>{t('state.verifying_data', 'Verifying employee data, please wait...')}</label>
                                                                </div>
                                                        }
                                                    </>
                                            }
                                        </div>
                                    </div>
                                </div>
                                :
                                <div className='ev_not_found text-center'>
                                    {
                                        (appState.stateLoad && !appState.success) ?
                                            <>
                                                <FcLeave size={96} />
                                                <p>{t('state.event_not_found', 'Sorry, this event is not active or the data you are looking for was not found.')}</p>
                                            </>
                                            : <></>
                                    }

                                </div>
                        }
                    </div>
                </div >
            </>:<><LoadingScreen /></>
            }
        </>

    )
}

export default memo(Event);
